import { Link } from "react-router-dom";
import { ArrowRight, Award, Handshake, Rocket, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";

const PILLARS = [
  { icon: Award,     tone: "am-tile-pink",     title: "Attention has value",      copy: "Streaming platforms burn billions on your time and give you nothing back. AudioMine is built on the opposite belief." },
  { icon: Handshake, tone: "am-tile-peach",    title: "Artists first, always",    copy: "Every RPM mined by a fan increases the reward loop for the artist who made the moment possible." },
  { icon: Rocket,    tone: "am-tile-mint",     title: "Web-native, fan-owned",    copy: "As the community grows, so does the monetary value of RPM. You're not a data-point — you're a contributor." },
  { icon: Volume2,   tone: "am-tile-lavender", title: "Sound that matters",       copy: "Full-fidelity D1 Audio powers every playback. Music is meant to be heard — not compressed to death." },
];

const FAQS = [
  { q: "What exactly is RPM?", a: "RPM stands for Reward-Performance Marker. It's a token embedded in a track or media asset that AudioMine detects. When you listen, watch, or play with RPM-enabled content, you mine those tokens. You collect them, and soon you'll be able to redeem or monetize them." },
  { q: "Do I need special hardware?", a: "No. AudioMine runs on your phone. Just aim it at any sound source and tap LiveLink. That's it." },
  { q: "How do artists earn from this?", a: "Artists embed RPM into their tracks and media. As fans interact with that content, the artist gets a direct signal — and the platform routes rewards accordingly. It's attention monetization the fair way." },
  { q: "Is this a streaming service?", a: "Not exactly. AudioMine is a music universe: identification, gamification, augmented listening, and rewards. It complements the music you already love, wherever it plays." },
  { q: "What does it cost fans?", a: "Nothing to start. You mine RPM for free by engaging with the app. Premium tiers and marketplace features will roll out as the community grows." },
  { q: "Is RPM crypto?", a: "RPM is a digital reward token maintained by AudioMine. Monetization pathways will be announced as the ecosystem matures — the goal is real value for real participation, without the crypto hype cycle." },
];

export default function WhyAudioMine() {
  return (
    <main data-testid="page-why" className="pt-24 md:pt-32">
      {/* HERO */}
      <section className="relative">
        <div className="container relative">
          <div className="am-blob" style={{ right: "5%", top: "-30px" }} />
          <div className="max-w-4xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-primary font-semibold border border-black/5">
                Why AudioMine
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-6 font-display font-bold tracking-tight text-[clamp(2.6rem,6.5vw,5.75rem)] leading-[0.98]">
                Music platforms took a lot.
                <br /><span className="font-serif-editorial italic font-normal">We're giving it </span>
                <span className="am-sunset-text">back.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-lg md:text-xl text-foreground/65 max-w-2xl leading-relaxed">
                The internet is packed with social media platforms and streaming services that do very little to reward the user for their time. AudioMine values your participation — and rewards it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32">
        <div className="container grid grid-cols-1 md:grid-cols-2 gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={i * 90}>
                <div className={`relative overflow-hidden rounded-[32px] ${p.tone} p-10 h-full flex gap-6`}>
                  <div className="h-14 w-14 shrink-0 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center text-foreground">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-2xl md:text-3xl leading-tight">{p.title}</div>
                    <p className="mt-3 text-foreground/70 leading-relaxed">{p.copy}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24 md:pb-32">
        <div className="container">
          <SectionHeading
            eyebrow="FAQ"
            title={<>Answers, <span className="font-serif-editorial italic font-normal">no fluff.</span></>}
            subtitle="Everything the curious visitor typically asks — condensed."
          />
          <Reveal delay={100}>
            <div className="mt-14 am-soft rounded-[32px] p-4 md:p-8 bg-white">
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-black/5">
                    <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-display font-semibold text-lg md:text-xl hover:text-primary hover:no-underline py-6">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-foreground/65 leading-relaxed text-base pb-6">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="container text-center flex flex-col items-center gap-6">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-5xl max-w-2xl">
              Convinced? <span className="font-serif-editorial italic font-normal">Let's build the future</span> of listening together.
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <Link to="/contact">
              <Button data-testid="why-cta" size="lg" className="rounded-full h-14 px-10 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_10px_30px_-8px_rgba(250,37,104,0.55)]">
                Get in touch <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
